import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Ref } from 'vue';

import { fetchCustomerFeatures } from '@/api/features';
import { fetchPoolmirrorCrawledJobs } from '@/api/poolmirror';

export type FeatureParamters = { [key: string]: any };
export type FeatureMap = { [key: string]: FeatureParamters };

export interface FeaturePoolMirrorCampaign {
  active_campaign_ids?: string;
  override_reference_number?: string;
  publication_minimum_duration?: number;
  logo_url?: string;
}

export interface FeatureOrderManagment {
  provider_selection_mode: 'none' | 'product' | 'provider';
  cost_passthrough?: boolean;
  cpc_t?: number;
  min_total_budget?: number;
  max_total_budget?: number;
  min_daily_budget?: number;
  max_daily_budget?: number;
  min_duration_in_days?: number;
  max_duration_in_days?: number;
  display_cost?: boolean;
  display_budget?: boolean;
  allow_previews?: boolean;
  campaign_management_read_only?: boolean;
  campaign_allow_finishing?: boolean;
  self_service?: boolean;
  auto_finalize_orders?: boolean;
}

export interface FeaturePromptsuite {
  conversation_limit?: number;
}

export interface FeatureExternalJobMarket {
  active_datapool_ids: number[];
  name: string;
}

export interface FeatureBudgetManagement {
  bound_by_budget?: boolean;
  expiring_budget_subscribers?: string[];
}

export interface FeatureEcDataVault {
  collect_candidate_data?: boolean;
}

export const useCustomerFeaturesStore = defineStore('customerFeatures', () => {
  const features: Ref<FeatureMap> = ref({});
  const isLoading = ref(false);
  const hasLoaded = ref(false);
  let pendingFetch: Promise<void> | null = null;

  async function fetchFeatures() {
    if (pendingFetch) return pendingFetch;

    isLoading.value = true;
    pendingFetch = (async () => {
      try {
        const response = await fetchCustomerFeatures();
        const tmp = {};

        for (const feature of response.data) {
          tmp[feature.type] = feature.parameters;
        }

        features.value = tmp;
      } catch (error) {
        console.error('Error fetching features:', error);
      } finally {
        isLoading.value = false;
        hasLoaded.value = true;
        pendingFetch = null;
      }
    })();

    return pendingFetch;
  }

  function hasFeature(feature: string): boolean {
    return typeof features.value[feature] !== 'undefined';
  }

  const featurePoolMirrorCampaign = computed(() => {
    return features.value.poolmirror_campaigns as FeaturePoolMirrorCampaign | undefined;
  });

  const featurePoolMirrorCampaigns = async () => {
    const feature = features.value.poolmirror_campaigns as FeaturePoolMirrorCampaign | undefined;

    const exploded = feature?.active_campaign_ids?.split(',').map((el) => {
      return parseInt(el);
    });

    const promises: Promise<any>[] = [];
    for (const el of exploded || []) {
      promises.push(fetchPoolmirrorCrawledJobs(el));
    }
    return await Promise.all(promises);
  };

  const featureOrderManagment = computed(() => {
    return features.value.order_management as FeatureOrderManagment | undefined;
  });

  const featureCanCreateEditOrder = computed(() => {
    const omFeature = features.value.order_management as FeatureOrderManagment | undefined;
    return omFeature && !omFeature.campaign_management_read_only;
  });

  const featureCanFinishCampaigns = computed(() => {
    const omFeature = features.value.order_management as FeatureOrderManagment | undefined;
    return omFeature && omFeature.campaign_allow_finishing;
  });

  const featurePromptsuite = computed(() => {
    return features.value.promptsuite as FeaturePromptsuite | undefined;
  });

  const featureExternalJobMarket = computed(() => {
    return features.value.external_job_market as FeatureExternalJobMarket | undefined;
  });

  const featureBudgetManagement = computed(() => {
    return features.value.budget_management as FeatureBudgetManagement | undefined;
  });

  const featureBoundByBudget = computed(() => {
    return featureBudgetManagement.value?.bound_by_budget === true;
  });

  const featureEcDataVault = computed(() => {
    return features.value.ec_data_vault as FeatureEcDataVault | undefined;
  });

  return {
    features,
    isLoading,
    hasLoaded,
    fetchFeatures,
    hasFeature,
    featurePoolMirrorCampaign,
    featurePoolMirrorCampaigns,
    featureOrderManagment,
    featureCanCreateEditOrder,
    featureCanFinishCampaigns,
    featurePromptsuite,
    featureExternalJobMarket,
    featureBudgetManagement,
    featureBoundByBudget,
    featureEcDataVault,
  };
});
